import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ShieldCheck, LogOut, CheckCircle2, XCircle } from "lucide-react";

const TOKEN_KEY = "bm_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");

  if (!token) {
    return <AdminLogin onLogin={(t) => setToken(t)} />;
  }
  return <AdminDashboard token={token} onLogout={() => setToken("")} />;
}

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <form
        onSubmit={handleSubmit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-lg border border-border bg-secondary/20 p-6"
      >
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-brand" />
          <h1 className="text-lg font-semibold text-foreground">Admin access</h1>
        </div>
        <input
          type="password"
          required
          autoFocus
          data-testid="admin-password-input"
          placeholder="Admin password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="h-10 w-full rounded-md border border-border bg-secondary/40 px-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring"
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-2 text-[13px] text-rose-400">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="mt-4 h-10 w-full bg-brand text-brand-foreground hover:bg-brand/90"
        >
          {loading ? "Checking..." : "Unlock"}
        </Button>
      </form>
    </div>
  );
}

const STATUS_STYLES = {
  active: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  pending_payment: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  expired: "text-rose-400 border-rose-500/30 bg-rose-500/10",
};

function AdminDashboard({ token, onLogout }) {
  const [ads, setAds] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/admin/ads", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setAds(res.data.ads);
        setRevenue(res.data.revenue);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          onLogout();
        } else {
          setError("Failed to load bookings.");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const updateStatus = async (adId, status) => {
    await api.patch(
      `/admin/ads/${adId}/status`,
      { status },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    load();
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  };

  return (
    <div className="min-h-screen bg-background px-4 py-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1100px]">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground">Ad bookings</h1>
          <Button
            onClick={logout}
            data-testid="admin-logout-button"
            variant="ghost"
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-4 w-4" /> Log out
          </Button>
        </div>

        {revenue && (
          <div
            data-testid="admin-revenue-card"
            className="mt-4 inline-block rounded-lg border border-border bg-secondary/20 px-4 py-3"
          >
            <div className="text-[12px] text-muted-foreground">Total ad revenue (paid bookings)</div>
            <div className="text-xl font-bold text-brand">{revenue.display}</div>
          </div>
        )}

        <div className="mt-6 overflow-x-auto rounded-lg border border-border/60">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : error ? (
            <div className="p-8 text-center text-rose-400">{error}</div>
          ) : ads.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No ad bookings yet.</div>
          ) : (
            <table className="w-full text-left text-[13px]">
              <thead className="border-b border-border/60 bg-secondary/20 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Brand</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Website</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Booked</th>
                  <th className="px-4 py-2">Expires</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => (
                  <tr key={ad.id} data-testid={`admin-ad-row-${ad.id}`} className="border-b border-border/40">
                    <td className="px-4 py-2.5 font-medium text-foreground">{ad.name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{ad.email}</td>
                    <td className="px-4 py-2.5">
                      <a
                        href={ad.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand hover:underline"
                      >
                        {ad.website_url}
                      </a>
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          STATUS_STYLES[ad.status] || STATUS_STYLES.pending_payment
                        }`}
                      >
                        {ad.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {new Date(ad.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {ad.expires_at ? new Date(ad.expires_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-2">
                        {ad.status !== "active" && (
                          <button
                            onClick={() => updateStatus(ad.id, "active")}
                            data-testid={`admin-activate-${ad.id}`}
                            className="flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-400 hover:bg-emerald-500/20"
                          >
                            <CheckCircle2 className="h-3 w-3" /> Activate
                          </button>
                        )}
                        {ad.status !== "expired" && (
                          <button
                            onClick={() => updateStatus(ad.id, "expired")}
                            data-testid={`admin-expire-${ad.id}`}
                            className="flex items-center gap-1 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[11px] font-medium text-rose-400 hover:bg-rose-500/20"
                          >
                            <XCircle className="h-3 w-3" /> Expire
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
